import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchEmployeeReferral,
  fetchReferralPanelCompanies,
  saveEmployeeReferral,
} from '../../services/employeeReferralService';
import { downloadProfileDocument, fetchProfile } from '../../services/profileService';
import '../Profile/Profile.css';
import {
  formatReferralSentForCandidate,
  getReferralSends,
  sortSendsNewestFirst,
} from '../../utils/referralSentDisplay';
import './EmployeeRefer.css';

const MAX_INTERESTED_JOBS = 20;

const emptyUrl = () => '';
const emptyCompanyGroup = () => ({ company: '', jobUrls: [emptyUrl()] });

const emptyForm = () => ({
  thirdPersonSelfIntroduction: '',
  interestedJobs: [emptyCompanyGroup()],
});

/** @param {string[]} optionsSorted */
const filterCompanySuggestions = (optionsSorted, query, limit = 50) => {
  const q = (query || '').trim().toLowerCase();
  const list = q.length
    ? optionsSorted.filter((name) => name.toLowerCase().includes(q))
    : optionsSorted;
  return list.slice(0, limit);
};

const normalizeJobsFromApi = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [emptyCompanyGroup()];
  }
  const groups = raw.map((g) => {
    const urls = Array.isArray(g?.jobUrls)
      ? g.jobUrls
          .map((u) => (typeof u === 'string' ? u : ''))
      : [];
    return {
      company: typeof g?.company === 'string' ? g.company : '',
      jobUrls: urls.length > 0 ? urls : [emptyUrl()],
    };
  });
  return groups.length > 0 ? groups : [emptyCompanyGroup()];
};

const normalizeHistoryEntryJobs = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((g) => ({
    company: typeof g?.company === 'string' ? g.company : '',
    jobUrls: Array.isArray(g?.jobUrls)
      ? g.jobUrls.map((u) => (typeof u === 'string' ? u : '')).filter(Boolean)
      : [],
  }));
};

const formatSubmissionTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
};

export default function EmployeeRefer() {
  const companySuggestBlurTimer = useRef(null);
  const [companySuggestGroupIndex, setCompanySuggestGroupIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [referralPanelCompanies, setReferralPanelCompanies] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [meta, setMeta] = useState({
    firstName: '',
    lastName: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    resumeDocumentId: null,
    resumeOriginalFilename: null,
    resumeSizeBytes: null,
  });

  const applyResponse = useCallback((data) => {
    setForm({
      thirdPersonSelfIntroduction: data.thirdPersonSelfIntroduction ?? '',
      interestedJobs: normalizeJobsFromApi(data.interestedJobs),
    });
    setMeta((m) => ({
      ...m,
      streetAddress: data.streetAddress ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      zip: data.zip ?? '',
      phone: data.phone ?? '',
      email: data.email ?? '',
    }));
    setSubmissionHistory(
      Array.isArray(data.submissionHistory)
        ? data.submissionHistory.map((e) => ({
            submittedAt: e.submittedAt ?? null,
            interestedJobs: normalizeHistoryEntryJobs(e.interestedJobs),
          }))
        : []
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchEmployeeReferral(),
      fetchProfile(),
      fetchReferralPanelCompanies().catch(() => []),
    ])
      .then(([referralData, profileData, companyList]) => {
        if (cancelled) return;
        setReferralPanelCompanies(Array.isArray(companyList) ? companyList : []);
        applyResponse(referralData);
        setForm((f) => ({
          ...f,
          thirdPersonSelfIntroduction:
            typeof profileData?.thirdPersonSelfIntroduction === 'string'
              ? profileData.thirdPersonSelfIntroduction
              : f.thirdPersonSelfIntroduction,
        }));
        const resumeDocs = Array.isArray(profileData?.documents)
          ? profileData.documents.filter((d) => d?.docType === 'RESUME')
          : [];
        const latestResume = resumeDocs.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];
        setMeta((m) => ({
          ...m,
          firstName: typeof profileData?.firstName === 'string' ? profileData.firstName : '',
          lastName: typeof profileData?.lastName === 'string' ? profileData.lastName : '',
          ...(latestResume
            ? {
                resumeDocumentId: latestResume.id,
                resumeOriginalFilename: latestResume.originalFilename,
                resumeSizeBytes: latestResume.sizeBytes,
              }
            : {}),
        }));
      })
      .catch(() => {
        if (!cancelled) setError('Could not load referral form.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyResponse]);

  useEffect(
    () => () => {
      if (companySuggestBlurTimer.current != null) {
        window.clearTimeout(companySuggestBlurTimer.current);
      }
    },
    []
  );

  const totalUrlCount = form.interestedJobs.reduce(
    (sum, g) => sum + (Array.isArray(g.jobUrls) ? g.jobUrls.length : 0),
    0
  );

  const onCompanyChange = (groupIndex) => (e) => {
    const company = e.target.value;
    setForm((f) => ({
      ...f,
      interestedJobs: f.interestedJobs.map((g, i) => (i === groupIndex ? { ...g, company } : g)),
    }));
    setSuccess(null);
  };

  const cancelCompanySuggestBlur = () => {
    if (companySuggestBlurTimer.current != null) {
      window.clearTimeout(companySuggestBlurTimer.current);
      companySuggestBlurTimer.current = null;
    }
  };

  const onCompanySuggestFocus = (groupIndex) => {
    cancelCompanySuggestBlur();
    setCompanySuggestGroupIndex(groupIndex);
  };

  const onCompanySuggestBlur = () => {
    cancelCompanySuggestBlur();
    companySuggestBlurTimer.current = window.setTimeout(() => {
      setCompanySuggestGroupIndex(null);
      companySuggestBlurTimer.current = null;
    }, 180);
  };

  const applyCompanySuggestion = (groupIndex, name) => {
    cancelCompanySuggestBlur();
    setCompanySuggestGroupIndex(null);
    setForm((f) => ({
      ...f,
      interestedJobs: f.interestedJobs.map((g, i) => (i === groupIndex ? { ...g, company: name } : g)),
    }));
    setSuccess(null);
  };

  const onJobUrlChange = (groupIndex, urlIndex) => (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      interestedJobs: f.interestedJobs.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return {
          ...g,
          jobUrls: g.jobUrls.map((u, ui) => (ui === urlIndex ? value : u)),
        };
      }),
    }));
    setSuccess(null);
  };

  const addCompanyGroupAfter = (groupIndex) => {
    setForm((f) => {
      if (f.interestedJobs.length >= MAX_INTERESTED_JOBS) return f;
      const interestedJobs = [...f.interestedJobs];
      interestedJobs.splice(groupIndex + 1, 0, emptyCompanyGroup());
      return { ...f, interestedJobs };
    });
    setSuccess(null);
  };

  const removeCompanyGroup = (groupIndex) => {
    setForm((f) => {
      if (f.interestedJobs.length <= 1) return f;
      return {
        ...f,
        interestedJobs: f.interestedJobs.filter((_, i) => i !== groupIndex),
      };
    });
    setSuccess(null);
  };

  const addJobUrlAfter = (groupIndex, urlIndex) => {
    setForm((f) => {
      if (totalUrlCount >= MAX_INTERESTED_JOBS) return f;
      return {
        ...f,
        interestedJobs: f.interestedJobs.map((g, i) => {
          if (i !== groupIndex) return g;
          const jobUrls = [...g.jobUrls];
          jobUrls.splice(urlIndex + 1, 0, emptyUrl());
          return { ...g, jobUrls };
        }),
      };
    });
    setSuccess(null);
  };

  const removeJobUrl = (groupIndex, urlIndex) => {
    setForm((f) => ({
      ...f,
      interestedJobs: f.interestedJobs.map((g, i) => {
        if (i !== groupIndex) return g;
        if (g.jobUrls.length <= 1) return g;
        return { ...g, jobUrls: g.jobUrls.filter((_, j) => j !== urlIndex) };
      }),
    }));
    setSuccess(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const interestedJobs = form.interestedJobs.map((group) => ({
        company: (group.company || '').trim(),
        jobUrls: (group.jobUrls || []).map((url) => (url || '').trim()),
      }));
      const nonEmptyRows = interestedJobs
        .map((group) => ({
          company: group.company,
          jobUrls: group.jobUrls.filter((url) => url),
        }))
        .filter((group) => group.company || group.jobUrls.length > 0);
      const hasAtLeastOneUrl = nonEmptyRows.some((group) => group.jobUrls.length > 0);
      if (!hasAtLeastOneUrl) {
        setError('Add at least one interested job.');
        return;
      }
      const data = await saveEmployeeReferral({
        interestedJobs,
      });
      applyResponse(data);
      setSuccess('Saved.');
      window.dispatchEvent(new Event('uplift-profile-updated'));
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Save failed.';
      setError(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const onDownloadResume = async () => {
    setError(null);
    try {
      if (!meta.resumeDocumentId) return;
      await downloadProfileDocument(meta.resumeDocumentId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Download failed.');
    }
  };

  if (loading) {
    return (
      <div className="employee-refer">
        <p className="employee-refer__loading">Loading…</p>
      </div>
    );
  }

  return (
    <div className="employee-refer">
      <h2>Employee referral</h2>

      {error && <div className="employee-refer__error">{error}</div>}
      {success && <div className="employee-refer__success">{success}</div>}

      <form onSubmit={onSubmit}>
        <details className="profile-card profile-card--compact employee-refer__contact-stack employee-refer__contact-collapsible">
          <summary className="employee-refer__contact-summary">
            <span className="employee-refer__contact-summary-title">Personal Info for Referral</span>
            <span className="employee-refer__contact-summary-meta" aria-hidden="true">
              <span className="employee-refer__contact-names">
                {[meta.firstName, meta.lastName].filter(Boolean).join(' ') || '—'}
              </span>
              <span className="employee-refer__contact-chevron" />
            </span>
          </summary>
          <div className="employee-refer__contact-collapsible-body">
          <div className="profile-row profile-row--2">
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-first">First name</label>
              <input id="er-sync-first" type="text" readOnly value={meta.firstName || '—'} tabIndex={-1} />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-last">Last name</label>
              <input id="er-sync-last" type="text" readOnly value={meta.lastName || '—'} tabIndex={-1} />
            </div>
          </div>
          <div className="profile-row profile-row--2">
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-email">Email</label>
              <input id="er-sync-email" type="text" readOnly value={meta.email || '—'} tabIndex={-1} />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-phone">Phone</label>
              <input id="er-sync-phone" type="tel" readOnly value={meta.phone || '—'} tabIndex={-1} />
            </div>
          </div>

          <h4 className="employee-refer__section-title">Address</h4>
          <div className="profile-field profile-field--compact profile-field--block">
            <label htmlFor="er-sync-street">Street</label>
            <textarea
              id="er-sync-street"
              rows={2}
              readOnly
              value={meta.streetAddress || '—'}
              tabIndex={-1}
            />
          </div>
          <div className="profile-row profile-row--3">
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-city">City</label>
              <input id="er-sync-city" type="text" readOnly value={meta.city || '—'} tabIndex={-1} />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-state">State</label>
              <input id="er-sync-state" type="text" readOnly value={meta.state || '—'} tabIndex={-1} />
            </div>
            <div className="profile-field profile-field--compact">
              <label htmlFor="er-sync-zip">ZIP</label>
              <input id="er-sync-zip" type="text" readOnly value={meta.zip || '—'} tabIndex={-1} />
            </div>
          </div>

          <h4 className="employee-refer__section-title">Third-party self introduction</h4>
          <div className="profile-field profile-field--compact profile-field--block">
            <textarea id="er-sync-intro" rows={5} readOnly value={form.thirdPersonSelfIntroduction || '—'} tabIndex={-1} />
          </div>

          <h4 className="employee-refer__section-title">Resume</h4>
          <div className="employee-refer__resume-actions">
            <span className="employee-refer__resume-meta">
              Resume: {meta.resumeOriginalFilename || 'Not uploaded in profile'}
              {meta.resumeSizeBytes != null ? ` · ${(meta.resumeSizeBytes / 1024).toFixed(1)} KB` : ''}
            </span>
            <button
              type="button"
              className="employee-refer__btn employee-refer__btn--ghost"
              disabled={!meta.resumeDocumentId}
              onClick={onDownloadResume}
            >
              Download resume
            </button>
          </div>
          <p className="employee-refer__profile-link-wrap">
            <Link to="/profile" className="employee-refer__profile-link">
              Click to edit
            </Link>
          </p>
          </div>
        </details>

        <div className="profile-card">
          <h3>Interested jobs</h3>
          <div className="employee-refer__interested-list">
            {form.interestedJobs.map((group, groupIndex) => {
              const suggestions = filterCompanySuggestions(referralPanelCompanies, group.company);
              const suggestOpen =
                companySuggestGroupIndex === groupIndex && suggestions.length > 0;
              return (
              <div key={groupIndex} className="employee-refer__interested-group">
                <div className="employee-refer__interested-row">
                  <div className="profile-field profile-field--compact employee-refer__interested-company">
                    <label htmlFor={`er-ij-co-${groupIndex}`}>Company</label>
                    <div className="employee-refer__company-autocomplete">
                      <input
                        id={`er-ij-co-${groupIndex}`}
                        type="text"
                        value={group.company}
                        onChange={onCompanyChange(groupIndex)}
                        onFocus={() => onCompanySuggestFocus(groupIndex)}
                        onBlur={onCompanySuggestBlur}
                        autoComplete="off"
                        aria-expanded={suggestOpen}
                        aria-controls={
                          suggestOpen ? `er-ij-co-suggest-${groupIndex}` : undefined
                        }
                        aria-autocomplete="list"
                        role="combobox"
                      />
                      {suggestOpen ? (
                        <ul
                          className="employee-refer__company-suggest"
                          id={`er-ij-co-suggest-${groupIndex}`}
                          role="listbox"
                        >
                          {suggestions.map((name) => (
                            <li key={name} role="presentation">
                              <button
                                type="button"
                                role="option"
                                tabIndex={-1}
                                className="employee-refer__company-suggest-option"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => applyCompanySuggestion(groupIndex, name)}
                              >
                                {name}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                  <div className="employee-refer__interested-actions">
                    <button
                      type="button"
                      className="employee-refer__row-btn"
                      onClick={() => addCompanyGroupAfter(groupIndex)}
                      disabled={form.interestedJobs.length >= MAX_INTERESTED_JOBS}
                      aria-label="Add company below"
                      title="Add company below"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="employee-refer__row-btn"
                      onClick={() => removeCompanyGroup(groupIndex)}
                      disabled={form.interestedJobs.length <= 1}
                      aria-label="Remove company"
                      title="Remove company"
                    >
                      −
                    </button>
                  </div>
                </div>
                <div className="employee-refer__interested-urls">
                  {group.jobUrls.map((url, urlIndex) => (
                    <div key={urlIndex} className="employee-refer__interested-url-row">
                      <div className="profile-field profile-field--compact employee-refer__interested-url">
                        <label htmlFor={`er-ij-url-${groupIndex}-${urlIndex}`}>
                          Job URL {urlIndex + 1}
                        </label>
                        <textarea
                          id={`er-ij-url-${groupIndex}-${urlIndex}`}
                          rows={2}
                          placeholder="https://…"
                          value={url}
                          onChange={onJobUrlChange(groupIndex, urlIndex)}
                          autoComplete="off"
                        />
                      </div>
                      <div className="employee-refer__interested-actions">
                        <button
                          type="button"
                          className="employee-refer__row-btn"
                          onClick={() => addJobUrlAfter(groupIndex, urlIndex)}
                          disabled={totalUrlCount >= MAX_INTERESTED_JOBS}
                          aria-label="Add job URL below"
                          title="Add job URL below"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          className="employee-refer__row-btn"
                          onClick={() => removeJobUrl(groupIndex, urlIndex)}
                          disabled={group.jobUrls.length <= 1}
                          aria-label="Remove job URL"
                          title="Remove job URL"
                        >
                          −
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className="profile-actions profile-actions--compact employee-refer__submit-wrap">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Request Referal'}
          </button>
        </div>
      </form>

      <br>
      </br>
      <section
        className="profile-card employee-refer__history-card employee-refer__history-card--readonly"
        aria-label="Referral request history"
      >
        <h3>Referral request history</h3>
        <p className="employee-refer__history-lead">Read-only record of your submitted requests and when referrals were sent.</p>
        {submissionHistory.length === 0 ? (
          <p className="employee-refer__history-empty">
            No requests yet. After you submit a referral request, it appears here with companies, job links, and send
            updates.
          </p>
        ) : (
          <ul className="employee-refer__history-list">
            {submissionHistory.map((entry, hi) => (
              <li key={hi} className="employee-refer__history-item">
                <div className="employee-refer__history-item-head">{formatSubmissionTime(entry.submittedAt)}</div>
                <div className="employee-refer__history-companies">
                  {(entry.interestedJobs || []).map((g, gi) => {
                    const sends = sortSendsNewestFirst(getReferralSends(g));
                    const jobUrls = (g.jobUrls || []).filter((u) => typeof u === 'string' && u.trim());
                    return (
                      <div key={`${hi}-c-${gi}`} className="employee-refer__history-company">
                        <div className="employee-refer__history-company-name">{g.company || '—'}</div>
                        {jobUrls.length > 0 ? (
                          <ul className="employee-refer__history-urls">
                            {jobUrls.map((url, ui) => (
                              <li key={`${hi}-${gi}-${ui}-${url.slice(0, 48)}`}>
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                  {url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="employee-refer__history-no-urls">No job URLs</p>
                        )}
                        {sends.length > 0 ? (
                          <ul className="employee-refer__history-sent-list">
                            {sends.map((s, si) => (
                              <li key={`${hi}-${gi}-sent-${si}-${s.sentAt}`} className="employee-refer__history-sent-item">
                                {formatReferralSentForCandidate(s)}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
